import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Breadcrumbs } from '../../components/layout/Breadcrumbs';
import { PageTitle } from '../../components/layout/PageTitle';
import { Button, Card, Checkbox, Heading, SelectField, Tag, TextField, ToastContainer, useToast } from '../../components/ui/index';
import { loadOrganisationDetails, saveOrganisationDetails, type OrganisationScenario } from './organisationDetailsApi';
import { organisationDetailsFixture, type OrganisationDetails, type OrganisationFieldError, type OrganisationPermissions } from './organisationDetailsModel';

interface OrganisationDetailsScreenProps {
  scenario?: OrganisationScenario;
}

const timezoneOptions = [
  'Australia/Sydney',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
].map((timezone) => ({ label: timezone, value: timezone }));

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Terminated', value: 'Terminated' },
];

const fieldStatus = (errors: OrganisationFieldError, field: keyof OrganisationDetails): 'critical' | undefined => {
  return errors[field] ? 'critical' : undefined;
};

const PermissionDenied = () => {
  return (
    <Card>
      <div className="flex flex-col gap-2">
        <Heading level={2}>Organisation configuration access required</Heading>
        <p className="m-0 text-neutral-secondary-foreground">You do not have permission to view or mutate restricted Organisation configuration. Ask a payroll configuration administrator for access.</p>
      </div>
    </Card>
  );
};

const ReadOnlyValue = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-sm border border-border-neutral-soft bg-neutral-soft p-3">
      <div className="text-xs font-semibold uppercase text-neutral-secondary-foreground">{label}</div>
      <div className="mt-1 text-sm text-neutral-foreground">{value}</div>
    </div>
  );
};

const StatusSummary = ({ details, permissions }: { details: OrganisationDetails; permissions: OrganisationPermissions }) => {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <Card>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-neutral-secondary-foreground">Lifecycle status</span>
          <Tag tone={details.status === 'Active' ? 'positive' : 'critical'}>{details.status}</Tag>
          {!permissions.canEditRestrictedStatus && <span className="text-sm text-neutral-secondary-foreground">Restricted status changes require elevated access.</span>}
        </div>
      </Card>
      <Card>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-neutral-secondary-foreground">STP reporter</span>
          <Tag tone={details.isStpReporter ? 'brand' : 'neutral'}>{details.isStpReporter ? 'Enabled' : 'Not enabled'}</Tag>
          <span className="text-sm text-neutral-secondary-foreground">ABN becomes mandatory when enabled.</span>
        </div>
      </Card>
      <Card>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-neutral-secondary-foreground">Effective from</span>
          <span className="text-sm font-semibold">{details.effectiveAt}</span>
          <span className="text-sm text-neutral-secondary-foreground">Backend owns audit and point-in-time reconstruction.</span>
        </div>
      </Card>
    </div>
  );
};

export const OrganisationDetailsScreen = ({ scenario }: OrganisationDetailsScreenProps) => {
  const [searchParams] = useSearchParams();
  const routeScenario = (scenario ?? searchParams.get('scenario') ?? 'default') as OrganisationScenario;
  const [details, setDetails] = useState<OrganisationDetails>(organisationDetailsFixture);
  const [errors, setErrors] = useState<OrganisationFieldError>({});
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<OrganisationPermissions>({ canEdit: false, canEditRestrictedStatus: false, canView: true });
  const [saving, setSaving] = useState(false);
  const { addToast, removeToast, toasts } = useToast();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    void loadOrganisationDetails(routeScenario).then((state) => {
      if (!mounted) return;
      setDetails(state.details);
      setPermissions(state.permissions);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [routeScenario]);

  const canSave = !loading && permissions.canEdit && permissions.canView;
  const brandingIsDefault = !details.defaultLogoImageId && !details.brandPrimaryHex && !details.brandAccentHex && !details.brandSecondaryHex;
  const showAbnRequirement = details.isStpReporter && !details.abn;
  const immutableValues = useMemo(() => [
    { label: 'Region', value: details.regionCode },
    { label: 'Default currency', value: details.defaultCurrency },
    { label: 'Default timezone', value: details.defaultTimezone },
  ], [details.defaultCurrency, details.defaultTimezone, details.regionCode]);

  const update = <K extends keyof OrganisationDetails>(field: K, value: OrganisationDetails[K]) => {
    setDetails((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const updateContact = (field: keyof OrganisationDetails['primaryContact'], value: string) => {
    setDetails((current) => ({ ...current, primaryContact: { ...current.primaryContact, [field]: value } }));
  };

  const updateAddress = (field: keyof OrganisationDetails['registeredBusinessAddress'], value: string) => {
    setDetails((current) => ({ ...current, registeredBusinessAddress: { ...current.registeredBusinessAddress, [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await saveOrganisationDetails(details);
    setSaving(false);

    if (!result.ok) {
      setErrors(result.errors ?? {});
      addToast('Organisation details could not be saved.', 'critical');
      return;
    }

    setErrors({});
    addToast('Organisation details saved.');
  }

  return (
    <>
      <Breadcrumbs items={[{ label: 'Payroll' }, { label: 'Organisation' }, { label: 'Organisation Details' }]} />
      <PageTitle
        actions={canSave ? <Button loading={saving} onClick={() => void handleSave()}>Save changes</Button> : undefined}
        subtitle="Maintain tenant-wide identity, defaults, contact details, lifecycle status, effective dating, and branding."
        title="Organisation Details"
      />

      {loading && <Card><p className="m-0">Loading Organisation Details...</p></Card>}
      {!loading && !permissions.canView && <PermissionDenied />}
      {!loading && permissions.canView && (
        <div className="flex flex-col gap-5">
          <StatusSummary details={details} permissions={permissions} />

          <Card header={<Heading level={2}>Identity</Heading>}>
            <div className="grid gap-4 md:grid-cols-2">
              <ReadOnlyValue label="Organisation ID" value={details.organisationId} />
              <ReadOnlyValue label="Tenant ID" value={details.tenantId} />
              <TextField id="legalName" label="Legal name" value={details.legalName} onChange={(value) => update('legalName', value)} />
              <TextField id="tradingName" label="Trading name" value={details.tradingName} onChange={(value) => update('tradingName', value)} />
              <TextField id="abn" label="ABN" status={fieldStatus(errors, 'abn')} statusText={errors.abn} value={details.abn} onChange={(value) => update('abn', value)} />
              <TextField id="acn" label="ACN" status={fieldStatus(errors, 'acn')} statusText={errors.acn} value={details.acn} onChange={(value) => update('acn', value)} />
              <Checkbox checked={details.isStpReporter} helpText={showAbnRequirement ? 'ABN is required when the organisation is the STP reporter.' : 'Backend owns ABR validation before save.'} id="isStpReporter" label="Organisation is STP reporter" onChange={(checked) => update('isStpReporter', checked)} />
              <SelectField disabled={!permissions.canEditRestrictedStatus} id="status" label="Status" options={statusOptions} value={details.status} onChange={(value) => update('status', value as OrganisationDetails['status'])} />
            </div>
          </Card>

          <Card header={<Heading level={2}>Immutable defaults</Heading>}>
            <p className="mt-0 text-sm text-neutral-secondary-foreground">Region, default currency, and default timezone are provisioning values. The frontend renders them read-only; backend persistence rejects changes.</p>
            <div className="grid gap-3 md:grid-cols-3">
              {immutableValues.map((item) => <ReadOnlyValue key={item.label} label={item.label} value={item.value} />)}
            </div>
            <div className="mt-4">
              <SelectField disabled id="timezoneReference" label="Australian timezone reference" options={timezoneOptions} value={details.defaultTimezone} />
            </div>
          </Card>

          <Card header={<Heading level={2}>Contact and registered address</Heading>}>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField id="contactName" label="Contact name" value={details.primaryContact.name} onChange={(value) => updateContact('name', value)} />
              <TextField id="contactRole" label="Contact role" value={details.primaryContact.role} onChange={(value) => updateContact('role', value)} />
              <TextField id="contactEmail" label="Contact email" type="email" value={details.primaryContact.email} onChange={(value) => updateContact('email', value)} />
              <TextField id="contactPhone" label="Contact phone" value={details.primaryContact.phone} onChange={(value) => updateContact('phone', value)} />
              <TextField id="street" label="Street address" value={details.registeredBusinessAddress.street} onChange={(value) => updateAddress('street', value)} />
              <TextField id="suburb" label="Suburb" value={details.registeredBusinessAddress.suburb} onChange={(value) => updateAddress('suburb', value)} />
              <TextField id="state" label="State" value={details.registeredBusinessAddress.state} onChange={(value) => updateAddress('state', value)} />
              <TextField id="postcode" label="Postcode" value={details.registeredBusinessAddress.postcode} onChange={(value) => updateAddress('postcode', value)} />
              <TextField id="country" label="Country" value={details.registeredBusinessAddress.country} onChange={(value) => updateAddress('country', value)} />
            </div>
          </Card>

          <Card header={<Heading level={2}>Effective dating</Heading>}>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField id="effectiveAt" label="Effective date" status={fieldStatus(errors, 'effectiveAt')} statusText={errors.effectiveAt} type="date" value={details.effectiveAt} onChange={(value) => update('effectiveAt', value)} />
              <TextField id="retroactiveReason" label="Retroactive reason" helpText="Required by backend rules when effective date is before the transaction date." status={fieldStatus(errors, 'retroactiveReason')} statusText={errors.retroactiveReason} value={details.retroactiveReason} onChange={(value) => update('retroactiveReason', value)} />
            </div>
          </Card>

          <Card header={<Heading level={2}>Branding</Heading>}>
            {brandingIsDefault && <p className="mt-0 text-sm text-neutral-secondary-foreground">Platform default logo and colours are in use for null branding fields.</p>}
            <div className="grid gap-4 md:grid-cols-2">
              <TextField id="defaultLogoImageId" label="Default logo image ID" helpText="References an existing media-library image asset; upload and lifecycle management are out of scope." status={fieldStatus(errors, 'defaultLogoImageId')} statusText={errors.defaultLogoImageId} value={details.defaultLogoImageId} onChange={(value) => update('defaultLogoImageId', value)} />
              <TextField id="brandPrimaryHex" label="Primary brand colour" helpText="Nullable. Use #RRGGBB when supplied." status={fieldStatus(errors, 'brandPrimaryHex')} statusText={errors.brandPrimaryHex} value={details.brandPrimaryHex} onChange={(value) => update('brandPrimaryHex', value)} />
              <TextField id="brandAccentHex" label="Accent brand colour" helpText="Nullable. Use #RRGGBB when supplied." status={fieldStatus(errors, 'brandAccentHex')} statusText={errors.brandAccentHex} value={details.brandAccentHex} onChange={(value) => update('brandAccentHex', value)} />
              <TextField id="brandSecondaryHex" label="Secondary brand colour" helpText="Nullable. Use #RRGGBB when supplied." status={fieldStatus(errors, 'brandSecondaryHex')} statusText={errors.brandSecondaryHex} value={details.brandSecondaryHex} onChange={(value) => update('brandSecondaryHex', value)} />
            </div>
          </Card>

          <Card header={<Heading level={2}>Audit context</Heading>}>
            <div className="grid gap-3 md:grid-cols-2">
              <ReadOnlyValue label="Updated at" value={new Date(details.updatedAt).toLocaleString()} />
              <ReadOnlyValue label="Updated by" value={details.updatedByUserId} />
            </div>
          </Card>
        </div>
      )}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  );
}
